import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchProjectBySlug, fetchProjects } from '../lib/api';

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [otherProjects, setOtherProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lightbox State
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await fetchProjectBySlug(slug);
        if (!data) {
          // Redirect to 404 if project not found
          navigate('/404', { replace: true });
          return;
        }
        setProject(data);

        // Load other projects for recommendation
        const allProjects = await fetchProjects();
        setOtherProjects(allProjects.filter(p => p.project_slug !== slug).slice(0, 3));
      } catch (err) {
        console.error('Error loading project detail:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate]);

  // Update document metadata
  useEffect(() => {
    if (project) {
      document.title = `${project.title} - Røjsmose Isolering ApS`;
      const metaDesc = document.querySelector('meta[name="description"]');
      const plainTextSummary = project.description
        ? project.description.replace(/<[^>]*>/g, '').substring(0, 160)
        : '';
      if (metaDesc) {
        metaDesc.setAttribute('content', plainTextSummary);
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = plainTextSummary;
        document.head.appendChild(newMeta);
      }
    }
  }, [project]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: '' });

    // Honeypot check
    if (formData.website_url) {
      setFormStatus({
        type: 'success',
        message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `[Projekt: ${project.title}] ${formData.message}`,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.'
        });
        setFormData({ name: '', email: '', phone: '', message: '', website_url: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl under afsendelsen. Prøv venligst igen, eller ring til os direkte.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Lightbox Navigation
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = (e) => {
    e.stopPropagation();
    if (project && project.images) {
      setLightboxIndex((prev) => (prev + 1) % project.images.length);
    }
  };
  const prevImage = (e) => {
    e.stopPropagation();
    if (project && project.images) {
      setLightboxIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  if (!project) return null;

  const mainImage = project.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';
  const categoryLabel = project.terms?.category?.[0] || 'Isolering';

  return (
    <div className="bg-white py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="mb-8 flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm font-medium text-slate-500">
            <li>
              <Link to="/" className="hover:text-brand-orange transition-colors">
                Forside
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <i className="fa-solid fa-chevron-right text-[10px] text-slate-400" aria-hidden="true" />
              <Link to="/projekter" className="hover:text-brand-orange transition-colors">
                Projekter
              </Link>
            </li>
            <li className="flex items-center gap-2 text-slate-900 font-semibold" aria-current="page">
              <i className="fa-solid fa-chevron-right text-[10px] text-slate-400" aria-hidden="true" />
              <span className="truncate max-w-[200px] sm:max-w-none">{project.title}</span>
            </li>
          </ol>
        </nav>

        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/projekter"
            className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy hover:text-brand-orange transition-colors"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            Tilbage til alle projekter
          </Link>
        </div>

        {/* Project Title & Meta */}
        <div className="border-b border-slate-200 pb-8 mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-brand-orange/10 px-3 py-1 text-xs font-bold text-brand-orange uppercase tracking-wider">
              {categoryLabel}
            </span>
            {project.location && (
              <span className="inline-flex items-center gap-1.5 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
                {project.location}
              </span>
            )}
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-brand-navy sm:text-4xl lg:text-5xl">
            {project.title}
          </h1>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left/Main Column: Project Details & Gallery */}
          <div className="lg:col-span-2 space-y-10">
            {/* Main Featured Image */}
            <div className="relative aspect-video overflow-hidden bg-slate-100 border border-slate-200">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${mainImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={project.title}
                className="h-full w-full object-cover"
              />
            </div>

            {/* Project Description */}
            <div className="prose prose-slate max-w-none">
              <h2 className="text-xl font-bold text-brand-navy mb-4 border-b border-slate-100 pb-2">
                Om projektet
              </h2>
              <div
                className="text-slate-600 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: project.description || '' }}
              />
            </div>

            {/* Project Gallery (if multiple images) */}
            {project.images && project.images.length > 1 && (
              <div className="border-t border-slate-200 pt-10">
                <h3 className="text-lg font-bold text-brand-navy mb-4">
                  Projektgalleri
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {project.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => openLightbox(index)}
                      className="group relative aspect-square overflow-hidden bg-slate-100 border border-slate-200 transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=85,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`Projektbillede ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" aria-hidden="true" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-8">
            {/* Project Info Box */}
            <div className="bg-slate-50 border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-brand-navy mb-4 border-b border-slate-200 pb-2">
                Projektdetaljer
              </h3>
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="font-semibold text-slate-500">Kategori</dt>
                  <dd className="mt-1 font-bold text-brand-navy capitalize">{categoryLabel}</dd>
                </div>
                {project.location && (
                  <div>
                    <dt className="font-semibold text-slate-500">Lokation</dt>
                    <dd className="mt-1 font-bold text-brand-navy flex items-center gap-1.5">
                      <i className="fa-solid fa-location-dot text-brand-orange" aria-hidden="true" />
                      {project.location}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="font-semibold text-slate-500">Udført af</dt>
                  <dd className="mt-1 font-bold text-brand-navy">Røjsmose Isolering ApS</dd>
                </div>
              </dl>
            </div>

            {/* Quick Contact Form */}
            <div className="bg-brand-navy text-white p-6 border border-brand-navy">
              <h3 className="text-lg font-bold mb-2">Interesseret i lignende løsning?</h3>
              <p className="text-slate-300 text-xs mb-6">
                Udfyld formularen, så kontakter vi dig til en uforpligtende snak og et godt tilbud.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot */}
                <div className="sr-only absolute">
                  <label htmlFor="website_url">Honeypot</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1">
                    Navn *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange rounded-none"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange rounded-none"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange rounded-none"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1">
                    Besked *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={3}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Fortæl os kort om din opgave..."
                    className="w-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange rounded-none resize-none"
                  />
                </div>

                {formStatus.message && (
                  <div
                    className={`p-3 text-xs font-semibold ${
                      formStatus.type === 'success' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-bold py-2.5 px-4 text-sm uppercase tracking-wider transition-colors duration-200 disabled:opacity-50 rounded-none"
                >
                  {isSubmitting ? 'Sender...' : 'Send forespørgsel'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Other Projects / Recommendations */}
        {otherProjects.length > 0 && (
          <div className="border-t border-slate-200 mt-16 pt-16">
            <h2 className="text-2xl font-extrabold tracking-tight text-brand-navy mb-8">
              Andre udførte projekter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {otherProjects.map((proj) => {
                const projImg = proj.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';
                return (
                  <Link
                    key={proj.id}
                    to={`/projekter/${proj.project_slug}`}
                    className="group flex flex-col overflow-hidden border border-slate-200 bg-white hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/${projImg.replace('https://assets.auxodev.dk/', '')}`}
                        alt={proj.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <span className="text-[10px] font-bold text-brand-orange uppercase tracking-wider mb-1">
                        {proj.terms?.category?.[0] || 'Isolering'}
                      </span>
                      <h3 className="text-base font-bold text-brand-navy group-hover:text-brand-orange transition-colors line-clamp-1">
                        {proj.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {lightboxIndex !== null && project.images && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 animate-in fade-in duration-200"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:text-brand-orange text-3xl focus:outline-none p-2"
              aria-label="Luk visning"
            >
              <i className="fa-light fa-xmark" aria-hidden="true" />
            </button>

            {/* Prev Button */}
            <button
              onClick={prevImage}
              className="absolute left-6 text-white hover:text-brand-orange text-4xl focus:outline-none p-2 hidden sm:block"
              aria-label="Forrige billede"
            >
              <i className="fa-light fa-chevron-left" aria-hidden="true" />
            </button>

            {/* Image Container */}
            <div className="relative max-w-4xl max-h-[80vh] flex flex-col items-center justify-center">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${project.images[lightboxIndex].replace('https://assets.auxodev.dk/', '')}`}
                alt={`Billede ${lightboxIndex + 1}`}
                className="max-w-full max-h-[75vh] object-contain shadow-2xl select-none"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="text-white/70 text-sm mt-4 font-semibold">
                Billede {lightboxIndex + 1} af {project.images.length}
              </p>
            </div>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-6 text-white hover:text-brand-orange text-4xl focus:outline-none p-2 hidden sm:block"
              aria-label="Næste billede"
            >
              <i className="fa-light fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
